import ky from "ky";
import {
  lookupResponseSchema,
  verifyResponseSchema,
  authenticateResponseSchema,
  selectCompanyResponseSchema,
  validateInviteResponseSchema,
  acceptInviteResponseSchema,
  completeDeviceApprovalResponseSchema,
  lookupDeviceResponseSchema,
  LookupResponse,
  VerifyResponse,
  AuthenticateResponse,
  SelectCompanyResponse,
  Company,
  companySchema,
  ValidateInviteResponse,
  AcceptInviteResponse,
  CompleteDeviceApprovalResponse,
  LookupDeviceResponse,
  DeviceApprovalPayload,
} from "../schemas/auth";
import { z } from "zod";

const authApi = ky.extend({
  prefixUrl:
    typeof window !== "undefined"
      ? `${window.location.origin}/auth/api`
      : "http://localhost:3000/auth/api",
  retry: {
    limit: 0,
  },
});

const deviceApi = ky.extend({
  prefixUrl:
    typeof window !== "undefined"
      ? `${window.location.origin}`
      : "http://localhost:3000",
  retry: {
    limit: 0,
  },
});

export async function lookupEmail(
  email: string,
  turnstileToken: string
): Promise<LookupResponse> {
  const data = await authApi
    .post("lookup", {
      json: { email, turnstile_token: turnstileToken },
    })
    .json<unknown>();

  return lookupResponseSchema.parse(data);
}

export async function sendVerificationCode(email: string): Promise<void> {
  await authApi.post("verify/send", {
    json: { email },
  });
}

export async function verifyCode(
  email: string,
  code: string
): Promise<VerifyResponse> {
  const data = await authApi
    .post("verify", {
      json: { email, code },
    })
    .json<unknown>();

  return verifyResponseSchema.parse(data);
}

export async function authenticate(
  identityId: string,
  password: string,
  authRequest?: string,
  returnTo?: string,
  callbackUrl?: string
): Promise<AuthenticateResponse> {
  const body: Record<string, string> = {
    identity_id: identityId,
    password,
  };
  if (authRequest) {
    body.auth_request = authRequest;
  }
  if (returnTo) {
    body.return_to = returnTo;
  }
  if (callbackUrl) {
    body.callback_url = callbackUrl;
  }
  const data = await authApi
    .post("authenticate", {
      json: body,
    })
    .json<unknown>();

  return authenticateResponseSchema.parse(data);
}

export async function getCompanies(identityId: string): Promise<Company[]> {
  const data = await authApi
    .get("companies", {
      searchParams: { identity_id: identityId },
    })
    .json<unknown>();

  return z.array(companySchema).parse(data);
}

/**
 * Cross-portal chain typeahead search for the support login flow (ZC-5717).
 *
 * Replaces the portal-pick → per-portal chain-list two-step: a single query
 * searches chains across ALL portals at once. Each result carries its OWN
 * portal_uid. Results are hydrated into the same Company shape the chooser +
 * handleCompanySelect already consume; selectCompany re-validates server-side.
 *
 * Returns [] for short/empty queries (the backend enforces a min length too).
 */
export async function searchChains(
  identityId: string,
  query: string
): Promise<Company[]> {
  const data = await authApi
    .get("chains/search", {
      searchParams: { identity_id: identityId, q: query },
    })
    .json<{
      chains: { portal_uid: string; chain_code: number; name: string }[];
    }>();

  return data.chains.map((c) => {
    const isMaster = c.chain_code === -1;

    return {
      portal_uid: c.portal_uid,
      chain_code: c.chain_code,
      company_name: c.name,
      // The backend resolves the -1 master and re-validates the chosen chain
      // server-side on selection — no client id.
      portal_user_public_id: null,
      // Only the `-1` master may JIT-provision; non-master entries are pure
      // switch targets that provision nothing.
      requires_provisioning: isMaster,
      auto_provisioning_enabled: isMaster,
      is_support: true,
    };
  });
}

export async function selectCompany(
  identityId: string,
  portalUid: string,
  chainCode: number,
  authRequest?: string,
  returnTo?: string,
  callbackUrl?: string
): Promise<SelectCompanyResponse> {
  // chain_code identifies the company. The PORTALUSER may not exist yet — the
  // backend JIT-provisions it on selection (ZC-5581) — so we no longer send a
  // portal_user_public_id from the client.
  const body: Record<string, string | number> = {
    identity_id: identityId,
    portal_uid: portalUid,
    chain_code: chainCode,
  };
  if (authRequest) {
    body.auth_request = authRequest;
  }
  if (returnTo) {
    body.return_to = returnTo;
  }
  if (callbackUrl) {
    body.callback_url = callbackUrl;
  }
  const data = await authApi
    .post("select-company", {
      json: body,
    })
    .json<unknown>();

  return selectCompanyResponseSchema.parse(data);
}

export async function validateInvite(
  token: string
): Promise<ValidateInviteResponse> {
  const data = await authApi
    .post("invite/validate", {
      json: { token },
    })
    .json<unknown>();

  return validateInviteResponseSchema.parse(data);
}

export async function acceptInvite(
  token: string,
  password: string,
  authRequest?: string,
  returnTo?: string,
  callbackUrl?: string
): Promise<AcceptInviteResponse> {
  const body: Record<string, string> = { token, password };
  if (authRequest) {
    body.auth_request = authRequest;
  }
  if (returnTo) {
    body.return_to = returnTo;
  }
  if (callbackUrl) {
    body.callback_url = callbackUrl;
  }
  const data = await authApi
    .post("invite/accept", {
      json: body,
    })
    .json<unknown>();

  return acceptInviteResponseSchema.parse(data);
}

export async function sendPasswordReset(email: string): Promise<void> {
  await authApi.post("password-reset/send", {
    json: { email },
  });
}

export async function completeDeviceApproval(
  payload: DeviceApprovalPayload
): Promise<CompleteDeviceApprovalResponse> {
  const data = await deviceApi
    .post("device/approve", {
      json: payload,
    })
    .json<unknown>();

  return completeDeviceApprovalResponseSchema.parse(data);
}

export async function lookupDevice(
  userCode: string
): Promise<LookupDeviceResponse> {
  const data = await deviceApi
    .get("device/lookup", {
      searchParams: { user_code: userCode },
    })
    .json<unknown>();

  return lookupDeviceResponseSchema.parse(data);
}

export async function probeResume(): Promise<
  | { resumable: false }
  | { resumable: true; identityLabel: string; identityId: string }
> {
  const data = await authApi.get("resume").json<{
    resumable: boolean;
    identity_label?: string;
    identity_id?: string;
  }>();
  return data.resumable
    ? {
        resumable: true,
        identityLabel: data.identity_label ?? "",
        identityId: data.identity_id ?? "",
      }
    : { resumable: false };
}

export async function resumeContinue(
  authRequest?: string
): Promise<
  { redirectUri: string } | { identityLabel: string; companies: Company[] }
> {
  const data = await authApi
    .post(
      "resume/continue",
      authRequest ? { json: { auth_request: authRequest } } : {}
    )
    .json<unknown>();

  const parsed = z
    .union([
      z.object({ redirect_uri: z.string() }),
      z.object({
        identity_label: z.string(),
        companies: z.array(companySchema),
      }),
    ])
    .parse(data);

  if ("redirect_uri" in parsed) {
    return { redirectUri: parsed.redirect_uri };
  }
  return { identityLabel: parsed.identity_label, companies: parsed.companies };
}

export async function resumeLogout(): Promise<void> {
  await authApi.post("resume/logout");
}

export async function resumeSwitchUser(): Promise<void> {
  await authApi.post("resume/switch-user");
}
